<template>
    <div v-if="!feedbackGiven" class="detection-feedback-card">
        <div class="detection-feedback-content">
            <div class="feedback-state-container">
                <button class="not-found-button" @click="selectNotFound">
                    <ThumbsDownIcon class="thumbs-down-icon" />
                    Het juiste gebaar staat er niet tussen
                </button>
            </div>
        </div>
    </div>

    <div v-else class="feedback-success-banner">
        <p class="feedback-success-text">
            Bedankt voor je feedback! Dit helpt ons om de zoekresultaten te verbeteren.
        </p>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { ISignOverview } from '../../../../functions/src/shared/contracts/api/GlossContract';
import { detectionFeedbackFirestoreService } from '@/shared/services/DetectionFeedbackFirestoreService';
import ThumbsDownIcon from '@/assets/svg/thumbs-down.svg';
import { IDetectionFeedbackData } from '@/shared/interfaces/DetectionFeedback';
import { signFeedbackService } from '@/shared/services/SignFeedbackService';

@Component({ 
    components: { ThumbsDownIcon } 
})
export default class DetectionFeedback extends Vue {
    @Prop({ required: false, default: () => [] }) 
    searchResults!: ISignOverview[];

    @Prop({ required: false, default: null }) 
    sessionId!: string | null;

    @Prop({ required: false, default: 'search' }) 
    searchSource!: 'search' | 'camera';

    public feedbackGiven: boolean = false;
    public selectedCorrectResult: ISignOverview | null = null;
    public isSubmitting: boolean = false;

    public mounted() {
        if (this.sessionId) {
            const stored = sessionStorage.getItem(`feedback-given-${this.sessionId}`);
            if (stored === 'true') {
                this.feedbackGiven = true;
            }
        }
    }

    public selectCorrectResult(result: ISignOverview): void {
        this.selectedCorrectResult = result;
        this.submitFeedback(true);
    }

    public onResultSelected(result: ISignOverview): void {
        this.selectCorrectResult(result);
    }

    public selectNotFound(): void {
        this.selectedCorrectResult = null;
        this.submitFeedback(false);
    }

    public async submitFeedback(foundDesiredGesture: boolean): Promise<void> {
        if (this.isSubmitting || this.feedbackGiven) {
            return;
        }

        this.isSubmitting = true;

        const effectiveSessionId = this.sessionId || (signFeedbackService && signFeedbackService.sessionId) || 'unknown';
        const feedbackData: IDetectionFeedbackData = {
            found_desired_gesture: foundDesiredGesture,
            selected_correct_result: this.selectedCorrectResult,
            search_results: this.searchResults,
            session_id: effectiveSessionId,
            search_source: this.searchSource,
            timestamp: new Date().toISOString()
        };

        this.feedbackGiven = true;
        if (this.sessionId) {
            sessionStorage.setItem(`feedback-given-${this.sessionId}`, 'true');
        }
        
        await this.$nextTick();
        this.$emit('feedback-submitted', feedbackData);

        try {
            await detectionFeedbackFirestoreService.saveFeedback(feedbackData);
        } catch (error) {
            console.error('Error saving detection feedback to Firestore:', error);
            
            this.feedbackGiven = false;
            if (this.sessionId) {
                sessionStorage.removeItem(`feedback-given-${this.sessionId}`);
            }
            
            this.$bvToast.toast(
                'Er is een fout opgetreden bij het opslaan van je feedback. Probeer het opnieuw.',
                {
                    title: 'Fout',
                    variant: 'danger',
                    solid: true
                }
            );
        } finally {
            this.isSubmitting = false;
        }
    }

}
</script>

<style lang="scss" scoped>
.detection-feedback-card {
    margin: 0;
    display: flex;
    justify-content: center;
    
    .detection-feedback-content {
        display: flex;
        width: 100%;
        max-width: 400px;
        padding: 16px;
        justify-content: center;
        align-items: center;
    }
    
    .feedback-state-container {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
    }
    
    .not-found-button {
        display: flex;
        padding: 11px 16px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        
        border-radius: 4px;
        background: #F3705D;
        border: 1px solid rgba(121, 114, 114, 0.40);
        box-shadow: 1px 2px 2px 0 rgba(125, 103, 103, 0.35);
        color: #FFF;
        text-align: center;
        font-family: Roboto;
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
        cursor: pointer;
        transition: background-color 0.2s ease;
        
        &:hover {
            background: #e55a47;
        }
        
        &:focus {
            outline: none;
            box-shadow: 1px 2px 2px 0 rgba(125, 103, 103, 0.35), 0 0 0 2px rgba(243, 112, 93, 0.3);
        }
    }
    
    .thumbs-down-icon {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
    }
}

.feedback-success-banner {
    width: 100%;
    max-width: 1137px;
    padding: 16px 24px;
    background: #E8F0FF;
    border: 1px solid #B8D4FF;
    border-radius: 7px;
    margin: 0;
    box-sizing: border-box;
    
    .feedback-success-text {
        color: #273F7A;
        text-align: center;
        font-family: Roboto, sans-serif;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        margin: 0;
    }
}
</style>
